import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  AppNotification,
  NotificationsService,
  translateNotification,
} from '../../services/notifications.service';
import { SupabaseService } from '../../services/supabase.service';

interface Toast extends AppNotification {
  key: number;
}

/**
 * Bottom-right toast for LIVE-arriving in-app notifications (the realtime
 * stream in NotificationsService). Auto-dismisses after 6s; clicking routes
 * like the bell (admin → that club's requests inbox, athlete → my requests)
 * and marks the row read. Renders nothing while there are no toasts.
 */
@Component({
  selector: 'app-notification-toast',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    @if (toasts.length) {
      <div class="nt" role="status" aria-live="polite">
        @for (t of toasts; track t.key) {
          <button type="button" class="nt__card ap-focus" (click)="openToast(t)">
            <span class="nt__ico" aria-hidden="true"><i class="pi pi-bell"></i></span>
            <span class="nt__main">
              <span class="nt__title">{{ t.title }}</span>
              @if (t.body) {
                <span class="nt__body">{{ t.body }}</span>
              }
            </span>
            <span
              class="nt__close"
              role="button"
              tabindex="0"
              [attr.aria-label]="'notifications.dismiss' | translate"
              (click)="$event.stopPropagation(); dismiss(t)"
              (keydown.enter)="$event.stopPropagation(); dismiss(t)"
              >×</span
            >
          </button>
        }
      </div>
    }
  `,
  styles: [
    `
      .nt {
        position: fixed;
        right: 16px;
        bottom: 16px;
        z-index: var(--ap-z-toast);
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-width: min(360px, calc(100vw - 32px));
      }
      .nt__card {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        text-align: left;
        padding: 12px 14px;
        border: 1px solid var(--ap-hairline, #e5e5e5);
        border-radius: 14px;
        background: var(--ap-bg, #fff);
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
        cursor: pointer;
        animation: nt-in 0.25s ease;
      }
      @keyframes nt-in {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }
      .nt__ico {
        width: 32px;
        height: 32px;
        flex-shrink: 0;
        display: grid;
        place-items: center;
        border-radius: 10px;
        background: var(--ap-green-tint, #e8f5ec);
        color: var(--ap-green, #15803d);
      }
      .nt__main {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }
      .nt__title {
        font-size: 14px;
        font-weight: 600;
        color: var(--ap-ink, #1d1d1f);
      }
      .nt__body {
        font-size: 13px;
        color: var(--ap-ink-2, #424245);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .nt__close {
        margin-left: auto;
        flex-shrink: 0;
        color: var(--ap-ink-3, #86868b);
        font-size: 16px;
        line-height: 1;
        padding: 2px 4px;
      }
      @media (prefers-reduced-motion: reduce) {
        .nt__card {
          animation: none;
        }
      }
    `,
  ],
})
export class NotificationToastComponent implements OnInit, OnDestroy {
  private readonly notif = inject(NotificationsService);
  private readonly supabase = inject(SupabaseService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  toasts: Toast[] = [];
  private sub?: Subscription;
  private seq = 0;
  private adminClubIds: string[] = [];

  ngOnInit(): void {
    this.sub = this.notif.incoming$.subscribe((n) => this.push(n));
    void this.loadAdminClubs();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private async loadAdminClubs(): Promise<void> {
    try {
      const user = await this.supabase.getCurrentUser();
      if (!user) return;
      const clubs = await this.supabase.getUserClubs(user.id);
      this.adminClubIds = clubs
        .filter((c) => c.role === 'admin' || c.role === 'super_admin')
        .map((c) => c.club_id);
    } catch {
      // routing simply falls back to /requests
    }
  }

  private push(n: AppNotification): void {
    // Rows arrive with English title/body from the DB triggers — map known ones.
    const { title, body } = translateNotification(n, this.translate);
    const t: Toast = { ...n, title, body, key: ++this.seq };
    this.toasts = [...this.toasts, t].slice(-3); // at most 3 stacked
    setTimeout(() => this.dismiss(t), 6000);
  }

  dismiss(t: Toast): void {
    this.toasts = this.toasts.filter((x) => x.key !== t.key);
  }

  openToast(t: Toast): void {
    void this.notif.markRead(t);
    this.dismiss(t);
    if (t.club_id && this.adminClubIds.includes(t.club_id)) {
      void this.router.navigate(['/club', t.club_id, 'requests']);
    } else {
      void this.router.navigate(['/requests']);
    }
  }
}
